import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useCallback } from 'react';
import { eventFeedbackService, UpdateEventFeedbackRequest } from '@cobu/api-client';
import { analyticsService, AnalyticsEvent } from '@cobu/analytics';
import { logger } from '../utils/logger';

// Query keys for event feedback
export const EVENT_FEEDBACK_KEYS = {
  all: ['eventFeedback'] as const,
  pending: () => [...EVENT_FEEDBACK_KEYS.all, 'pending'] as const,
};

/**
 * Hook to fetch pending event feedback
 */
export const usePendingFeedbackQuery = (userUuid?: string) => {
  return useQuery({
    queryKey: EVENT_FEEDBACK_KEYS.pending(),
    queryFn: () => eventFeedbackService.getPendingFeedback(userUuid!),
    enabled: !!userUuid,
    staleTime: 0,
  });
};

/**
 * Hook to update event feedback
 */
export const useUpdateEventFeedbackMutation = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (request: UpdateEventFeedbackRequest) => 
      eventFeedbackService.updateEventFeedback(request),
    onSuccess: (_, variables) => {
      // Track analytics
      analyticsService.trackEvent(AnalyticsEvent.COMPLETE_FEEDBACK, {
        generalRating: variables.generalRating,
        feedbackLength: variables.feedbackInputText.length,
        type: variables.type,
      });
    },
    onError: (error, variables) => {
      logger.error('Update event feedback error:', error);
      
      /* ANALYTICS_DISABLED: EVENT_FEEDBACK_SUBMISSION_FAILED ('event_feedback_submission_failed')
        analyticsService.trackEvent(AnalyticsEvent.EVENT_FEEDBACK_SUBMISSION_FAILED, {
                generalRating: variables.generalRating,
                type: variables.type,
                error: error.message,
              });
        */
    },
  });
};

/**
 * Hook to update last reviewed timestamp
 */
export const useUpdateLastReviewedAtMutation = () => {
  return useMutation({
    mutationFn: ({ timestamp, userUuid }: { timestamp: string; userUuid: string }) => 
      eventFeedbackService.updateLastReviewedAt(timestamp, userUuid),
    onSuccess: () => {
      /* ANALYTICS_DISABLED: GOOGLE_REVIEW_TIMESTAMP_UPDATED ('google_review_timestamp_updated')
        analyticsService.trackEvent(AnalyticsEvent.GOOGLE_REVIEW_TIMESTAMP_UPDATED);
        */
    },
    onError: (error) => {
      logger.error('Update last reviewed timestamp error:', error);
      
      analyticsService.trackEvent(AnalyticsEvent.GOOGLE_REVIEW_TIMESTAMP_UPDATE_FAILED, {
        error: error.message,
      });
    },
  });
};

/**
 * Comprehensive hook for event feedback functionality
 */
export const useEventFeedback = (userUuid?: string) => {
  const pendingFeedbackQuery = usePendingFeedbackQuery(userUuid);
  const updateFeedbackMutation = useUpdateEventFeedbackMutation();
  const updateLastReviewedMutation = useUpdateLastReviewedAtMutation();

  const handleUpdateFeedback = useCallback(
    async (request: UpdateEventFeedbackRequest) => {
      await updateFeedbackMutation.mutateAsync(request);
    },
    [updateFeedbackMutation]
  );

  const handleUpdateLastReviewed = useCallback(
    async (timestamp: string) => {
      if (!userUuid) throw new Error('User UUID is required');
      await updateLastReviewedMutation.mutateAsync({ timestamp, userUuid });
    },
    [updateLastReviewedMutation, userUuid]
  );

  const handleDidNotAttend = useCallback(
    async (uuid: string, type: 'groupEvent' | 'post') => {
      await handleUpdateFeedback({
        uuid,
        generalRating: 0,
        feedbackInputText: '',
        type,
      });

      // Track did not attend event
      /* ANALYTICS_DISABLED: EVENT_DID_NOT_ATTEND ('event_did_not_attend')
          analyticsService.trackEvent(AnalyticsEvent.EVENT_DID_NOT_ATTEND, {
                  eventUuid: uuid,
                  type,
                });
          */
    },
    [handleUpdateFeedback]
  );

  return {
    // Data
    pendingFeedback: pendingFeedbackQuery.data,
    
    // Loading states
    isLoading: pendingFeedbackQuery.isLoading,
    isUpdating: updateFeedbackMutation.isPending,
    isUpdatingLastReviewed: updateLastReviewedMutation.isPending,
    
    // Error states
    error: pendingFeedbackQuery.error?.message || 
           updateFeedbackMutation.error?.message || 
           updateLastReviewedMutation.error?.message || 
           null,
    
    // Actions
    updateFeedback: handleUpdateFeedback,
    updateLastReviewed: handleUpdateLastReviewed,
    didNotAttend: handleDidNotAttend,
    
    // Utils
    refetch: pendingFeedbackQuery.refetch,
  };
};
